import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, Select } from "@repo/o37-ui";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, ArrowLeft, Calendar } from "lucide-react";
import { HoldingsBankAccount } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type DateFormat = 'auto' | 'mdy' | 'dmy' | 'ymd';

const DATE_FORMAT_OPTIONS: { value: DateFormat; label: string; example: string }[] = [
  { value: 'mdy', label: 'MM/DD/YYYY', example: '02/13/2026 → Feb 13, 2026' },
  { value: 'dmy', label: 'DD/MM/YYYY', example: '13/02/2026 → Feb 13, 2026' },
  { value: 'ymd', label: 'YYYY-MM-DD', example: '2026-02-13 → Feb 13, 2026' },
];

// Parse CSV content into rows
function parseCSV(content: string, dateFormat: DateFormat): any[] {
  const lines = content.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) return [];

  // Parse header row
  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());

  // Map common column names
  const columnMap: Record<string, string> = {
    'date': 'date',
    'transaction date': 'date',
    'trans date': 'date',
    'posted date': 'postedDate',
    'post date': 'postedDate',
    'amount': 'amount',
    'debit': 'amount',
    'credit': 'amount',
    'description': 'description',
    'memo': 'description',
    'name': 'vendorName',
    'vendor': 'vendorName',
    'merchant': 'vendorName',
    'payee': 'vendorName',
    'counterparty': 'vendorName',
    'category': 'category',
    'type': 'category',
    'card': 'card',
    'card member': 'cardholder',
    'cardholder': 'cardholder',
    'card holder': 'cardholder',
    'member': 'cardholder',
    'reference': 'referenceNumber',
    'reference number': 'referenceNumber',
    'confirmation': 'referenceNumber',
    'notes': 'notes',
  };

  // Map headers to our field names
  const fieldMap = headers.map(h => {
    for (const [key, value] of Object.entries(columnMap)) {
      if (h.includes(key)) return value;
    }
    return h.replace(/\s+/g, '_');
  });

  // Parse data rows
  const rows: any[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length === 0) continue;

    const row: any = {};
    fieldMap.forEach((field, idx) => {
      if (values[idx] !== undefined) {
        row[field] = values[idx].trim();
      }
    });

    // Ensure required fields
    if (!row.date && !row.postedDate) continue;
    if (!row.amount) continue;

    // Parse amount (handle negative, parentheses, currency symbols)
    let amount = row.amount.replace(/[$,]/g, '').trim();
    if (amount.startsWith('(') && amount.endsWith(')')) {
      amount = '-' + amount.slice(1, -1);
    }
    row.amount = parseFloat(amount);
    if (isNaN(row.amount)) continue;

    // Use posted date as date if no transaction date
    if (!row.date && row.postedDate) {
      row.date = row.postedDate;
    }

    // Parse date
    const parsedDate = parseDate(row.date, dateFormat);
    if (!parsedDate) continue;
    row.date = parsedDate;

    if (row.postedDate) {
      row.postedDate = parseDate(row.postedDate, dateFormat) || row.postedDate;
    }

    // Use description as vendor name if not set
    if (!row.vendorName) {
      row.vendorName = row.description || 'Unknown';
    }

    rows.push(row);
  }

  return rows;
}

// Parse a single CSV line handling quotes
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);

  return result.map(s => s.replace(/^"|"$/g, '').trim());
}

// Parse date with explicit format
function parseDate(dateStr: string, format: DateFormat): string | null {
  if (!dateStr) return null;

  // Normalize separators (treat - and / the same)
  const normalized = dateStr.trim().replace(/[-/]/g, '/');

  // Try ISO format first (always valid regardless of setting)
  if (/^\d{4}\/\d{2}\/\d{2}/.test(normalized) || /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.split('T')[0].replace(/\//g, '-');
  }

  // Parse based on selected format
  const match = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (match) {
    const [, part1, part2, part3] = match;
    let year: string, month: string, day: string;

    if (format === 'mdy') {
      // MM/DD/YYYY
      month = part1;
      day = part2;
      year = part3;
    } else if (format === 'dmy') {
      // DD/MM/YYYY
      day = part1;
      month = part2;
      year = part3;
    } else {
      // Auto-detect: If first part > 12, it's probably day (EU format)
      // Otherwise assume US format (more common in banking)
      const firstNum = parseInt(part1);
      if (firstNum > 12) {
        day = part1;
        month = part2;
      } else {
        month = part1;
        day = part2;
      }
      year = part3;
    }

    const fullYear = year.length === 2 ? '20' + year : year;
    const monthNum = parseInt(month);
    const dayNum = parseInt(day);

    // Validate month and day
    if (monthNum < 1 || monthNum > 12) return null;
    if (dayNum < 1 || dayNum > 31) return null;

    return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  // Try YYYY-MM-DD or YYYY/MM/DD format explicitly
  const ymdMatch = normalized.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return null;
}

// Format date for display
function formatDateDisplay(isoDate: string): string {
  try {
    const date = new Date(isoDate + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoDate;
  }
}

export default function ImportUpload() {
  const navigate = useNavigate();
  const [bankAccounts, setBankAccounts] = useState<HoldingsBankAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [dateFormat, setDateFormat] = useState<DateFormat>('mdy');
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message: string;
    importId?: string;
  } | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadBankAccounts();
  }, []);

  const loadBankAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/bank-accounts`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        const accounts = data
          .filter((a: any) => a.is_active)
          .map((a: any) => ({
            id: a.id,
            name: a.name,
            accountType: a.account_type,
            bankName: a.bank_name
          }));
        setBankAccounts(accounts);
        if (accounts.length === 1) {
          setSelectedAccountId(accounts[0].id);
        }
      }
    } catch (e) {
      console.error('Failed to load bank accounts:', e);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (selectedFile: File) => {
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'text/plain'];
    const validExtensions = ['.csv', '.txt'];
    const extension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));

    if (!validTypes.includes(selectedFile.type) && !validExtensions.includes(extension)) {
      setUploadResult({
        success: false,
        message: 'Invalid file type. Please upload a CSV file.'
      });
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);

    // Read file content for preview
    try {
      const content = await selectedFile.text();
      setFileContent(content);
      // Generate initial preview with default format
      const parsed = parseCSV(content, dateFormat);
      setPreviewData(parsed.slice(0, 5));
    } catch (e) {
      console.error('Failed to read file:', e);
    }
  };

  // Update preview when date format changes
  useEffect(() => {
    if (fileContent) {
      const parsed = parseCSV(fileContent, dateFormat);
      setPreviewData(parsed.slice(0, 5));
    }
  }, [dateFormat, fileContent]);

  const handleUpload = async () => {
    if (!file || !fileContent) return;

    setUploading(true);
    setUploadResult(null);

    try {
      const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

      if (extension === '.pdf') {
        setUploadResult({
          success: false,
          message: 'PDF parsing is not yet supported. Please export your statement as CSV.'
        });
        setUploading(false);
        return;
      }

      const parsedRows = parseCSV(fileContent, dateFormat);

      if (parsedRows.length === 0) {
        setUploadResult({
          success: false,
          message: 'No valid transactions found in the CSV file. Please check the file format.'
        });
        setUploading(false);
        return;
      }

      // Send parsed data as JSON
      const response = await fetch(`${API_URL}/holdings/imports/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({
          bankAccountId: selectedAccountId || null,
          fileName: file.name,
          fileType: extension.replace('.', ''),
          rawData: parsedRows
        })
      });

      const data = await response.json();

      if (response.ok) {
        const skippedMsg = data.skipped > 0 ? ` (${data.skipped} duplicates skipped)` : '';
        const transferMsg = data.transfersDetected > 0 ? ` ${data.transfersDetected} internal transfer(s) detected.` : '';
        setUploadResult({
          success: true,
          message: `Successfully imported ${data.imported} transactions from "${file.name}"${skippedMsg}.${transferMsg}`,
          importId: data.id
        });
        setFile(null);
      } else {
        setUploadResult({
          success: false,
          message: data.error || 'Failed to upload file'
        });
      }
    } catch (e: any) {
      setUploadResult({
        success: false,
        message: e.message || 'Failed to parse or upload file. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/holdings/bank-imports')}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Bank Imports</span>
        </button>

        <h1 className="text-2xl font-semibold mb-1">Import Bank Statement</h1>
        <p className="text-neutral-500">Upload a CSV bank or credit card statement to import transactions</p>
      </div>

      <div className="max-w-2xl">
        {/* Bank Account Selection */}
        {bankAccounts.length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Bank Account (Optional)
              </label>
              <Select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                <option value="">Select account...</option>
                {bankAccounts.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name} {a.bankName && `(${a.bankName})`}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-neutral-500 mt-2">
                Link this import to a specific bank account for better organization.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Upload Area */}
        <Card>
          <CardContent className="p-6">
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : file
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInput}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />

              {file ? (
                <div>
                  <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div className="font-medium text-neutral-900 mb-1">{file.name}</div>
                  <div className="text-sm text-neutral-500 mb-4">{formatFileSize(file.size)}</div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setFileContent('');
                      setPreviewData([]);
                      setUploadResult(null);
                    }}
                    className="text-sm text-red-600 hover:underline"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div>
                  <div className="w-16 h-16 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-neutral-400" />
                  </div>
                  <div className="font-medium text-neutral-900 mb-1">
                    Drop your file here or click to browse
                  </div>
                  <div className="text-sm text-neutral-500">
                    Supports CSV files
                  </div>
                </div>
              )}
            </div>

            {/* Date Format Selector - shown when file is selected */}
            {file && previewData.length > 0 && !uploadResult?.success && (
              <div className="mt-6 border border-neutral-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-neutral-500" />
                  <h3 className="font-medium text-neutral-900">Date Format</h3>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {DATE_FORMAT_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setDateFormat(opt.value)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                        dateFormat === opt.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-xs opacity-70">{opt.example}</div>
                    </button>
                  ))}
                </div>

                {/* Preview Table */}
                <div className="text-sm text-neutral-600 mb-2">Preview (first {previewData.length} rows):</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-neutral-50">
                        <th className="text-left p-2 border border-neutral-200 font-medium">Date</th>
                        <th className="text-left p-2 border border-neutral-200 font-medium">Amount</th>
                        <th className="text-left p-2 border border-neutral-200 font-medium">Vendor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, idx) => {
                        const dateValid = row.date && /^\d{4}-\d{2}-\d{2}$/.test(row.date);
                        return (
                          <tr key={idx}>
                            <td className={`p-2 border border-neutral-200 ${!dateValid ? 'bg-red-50 text-red-700' : ''}`}>
                              {dateValid ? formatDateDisplay(row.date) : row.date || '⚠️ Invalid'}
                            </td>
                            <td className="p-2 border border-neutral-200">
                              {typeof row.amount === 'number'
                                ? `$${Math.abs(row.amount).toFixed(2)}${row.amount < 0 ? ' (expense)' : ' (income)'}`
                                : row.amount}
                            </td>
                            <td className="p-2 border border-neutral-200">{row.vendorName || row.description || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {previewData.some(r => !r.date || !/^\d{4}-\d{2}-\d{2}$/.test(r.date)) && (
                  <div className="mt-2 text-xs text-red-600">
                    ⚠️ Some dates couldn't be parsed. Try a different date format above.
                  </div>
                )}
              </div>
            )}

            {/* Upload Result */}
            {uploadResult && (
              <div className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${
                uploadResult.success
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-red-50 text-red-700'
              }`}>
                {uploadResult.success ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <div className="font-medium">{uploadResult.success ? 'Success!' : 'Error'}</div>
                  <div className="text-sm">{uploadResult.message}</div>
                  {uploadResult.success && uploadResult.importId && (
                    <button
                      onClick={() => navigate('/holdings/bank-imports/review')}
                      className="text-sm underline mt-2"
                    >
                      Review transactions
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Upload Button */}
            {file && !uploadResult?.success && (
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  icon={uploading ? undefined : <Upload className="w-4 h-4" />}
                  text={uploading ? 'Uploading...' : 'Upload & Process'}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-6">
          <CardContent className="p-6">
            <h3 className="font-semibold text-neutral-900 mb-3">CSV Format</h3>
            <div className="space-y-3 text-sm text-neutral-600">
              <p>Export your bank or credit card statement as CSV. After uploading, you'll select the date format and preview the data before importing.</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-neutral-50 p-2 rounded">
                  <span className="font-medium">Date:</span> date, transaction date, posted date
                </div>
                <div className="bg-neutral-50 p-2 rounded">
                  <span className="font-medium">Amount:</span> amount, debit, credit
                </div>
                <div className="bg-neutral-50 p-2 rounded">
                  <span className="font-medium">Vendor:</span> name, merchant, payee, vendor
                </div>
                <div className="bg-neutral-50 p-2 rounded">
                  <span className="font-medium">Description:</span> description, memo
                </div>
                <div className="bg-neutral-50 p-2 rounded">
                  <span className="font-medium">Cardholder:</span> cardholder, card member
                </div>
                <div className="bg-neutral-50 p-2 rounded">
                  <span className="font-medium">Category:</span> category, type
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-800 mt-2">
                <strong>Date Formats:</strong> US banks typically use MM/DD/YYYY, European banks use DD/MM/YYYY. Check the preview to confirm dates are parsed correctly.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
